import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import TodayView from "@/components/TodayView";
import JourneyView from "@/components/JourneyView";
import SelfiesView from "@/components/SelfiesView";
import HealthView from "@/components/HealthView";
import WaterView from "@/components/WaterView";
import {
  isoDate,
  dayNumber,
  entryCompletion,
  computeStreak,
  plantStage,
  MORNING_ITEMS,
  NIGHT_ITEMS,
} from "@/lib/skincare";
import { Bell, Leaf } from "lucide-react";
import { celebrate, playPop } from "@/lib/fx";
import { subscribeToPush, pushSupported } from "@/lib/push";

export default function Dashboard() {
  const { user, setUser, logout } = useAuth();
  const [tab, setTab] = useState("today");
  const [entries, setEntries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const todayKey = isoDate(new Date());
  const [todayEntry, setTodayEntry] = useState({
    morning: {},
    night: {},
    symptoms: [],
    notes: "",
    mood: null,
    water: 0,
  });
  const saveTimer = useRef(null);

  const isIOS = useMemo(
    () => typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent),
    []
  );
  const isStandalone = useMemo(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true),
    []
  );
  const supportsNotif = typeof window !== "undefined" && "Notification" in window;
  const [notifGranted, setNotifGranted] = useState(
    supportsNotif && Notification.permission === "granted"
  );

  const morningItems = user?.morning_items?.length ? user.morning_items : MORNING_ITEMS;
  const nightItems = user?.night_items?.length ? user.night_items : NIGHT_ITEMS;

  // Bootstrap data
  useEffect(() => {
    (async () => {
      try {
        const [e, p, todayE] = await Promise.all([
          api.listEntries(),
          api.listPhotos(),
          api.getEntry(todayKey),
        ]);
        setEntries(e);
        setPhotos(p);
        setTodayEntry({
          morning: todayE?.morning || {},
          night: todayE?.night || {},
          symptoms: todayE?.symptoms || [],
          notes: todayE?.notes || "",
          mood: todayE?.mood ?? null,
          water: todayE?.water ?? 0,
        });
      } catch (err) {
        // silent
      }
    })();
  }, [todayKey]);

  // Listen for profile updates from child components (e.g., water goal)
  useEffect(() => {
    const handler = (e) => {
      setUser((prev) => (prev ? { ...prev, ...e.detail } : prev));
    };
    window.addEventListener("profile-updated", handler);
    return () => window.removeEventListener("profile-updated", handler);
  }, [setUser]);

  // Debounced save of today's entry
  const persist = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.saveEntry(todayKey, next);
        setEntries((prev) => {
          const others = prev.filter((x) => x.date !== todayKey);
          return [...others, { ...next, date: todayKey, user_id: user.user_id }];
        });
      } catch {}
    }, 400);
  }, [todayKey, user]);

  const updateEntry = (next, opts = {}) => {
    setTodayEntry(next);
    persist(next);
    if (opts.playSound) playPop(opts.checked !== false);
  };

  // Called by JourneyView when a past (or today's) entry is saved from the sheet
  const handleEntryUpdate = useCallback((date, updatedEntry) => {
    setEntries((prev) => {
      const others = prev.filter((x) => x.date !== date);
      return [...others, { ...updatedEntry, date, user_id: user?.user_id }];
    });
    if (date === todayKey) {
      setTodayEntry((prev) => ({ ...prev, ...updatedEntry }));
    }
  }, [todayKey, user]);

  const startDate = user?.start_date || "2025-06-16";
  const dayNum = useMemo(() => dayNumber(startDate, new Date()), [startDate]);
  const streak = useMemo(
    () => computeStreak([...entries, { ...todayEntry, date: todayKey }], new Date(), startDate, morningItems, nightItems),
    [entries, todayEntry, todayKey, startDate, morningItems, nightItems]
  );
  const totalCompleted = useMemo(() => {
    const all = [...entries.filter((x) => x.date !== todayKey), { ...todayEntry, date: todayKey }];
    return all.filter((e) => entryCompletion(e, morningItems, nightItems) >= 0.7).length;
  }, [entries, todayEntry, todayKey, morningItems, nightItems]);
  const stage = plantStage(streak, totalCompleted);

  // Track previously celebrated completion to fire toast once
  const celebratedRef = useRef(false);
  const waterCelebratedRef = useRef(false);
  useEffect(() => {
    const c = entryCompletion(todayEntry, morningItems, nightItems);
    if (c === 1 && !celebratedRef.current) {
      celebratedRef.current = true;
      toast.success("🌿 Day complete! Your plant grew a leaf.");
      celebrate("plant");
      if (notifGranted) {
        try {
          new Notification("Skin Garden", {
            body: "Day complete — your plant added a new leaf 🌿",
          });
        } catch {}
      }
    }
    if (c < 1) celebratedRef.current = false;

    const goal = user?.water_goal || 10;
    const w = todayEntry?.water || 0;
    if (w >= goal && !waterCelebratedRef.current) {
      waterCelebratedRef.current = true;
      toast.success(`💧 Hydration goal hit (${goal} glasses)!`);
      celebrate("water");
    }
    if (w < goal) waterCelebratedRef.current = false;
  }, [todayEntry, notifGranted, morningItems, nightItems, user]);

  const requestNotifications = async () => {
    if (isIOS && !isStandalone) {
      toast.message("📲 iOS: Add to Home Screen first", {
        description: "Tap Share → Add to Home Screen, then open from your home screen to enable reminders.",
        duration: 8000,
      });
      return;
    }
    if (!pushSupported()) {
      toast.message("Notifications not supported on this browser");
      return;
    }
    try {
      await subscribeToPush();
      setNotifGranted(true);
      toast.success("🌿 Reminders on! Morning at 8:00, night at 21:30.");
    } catch (err) {
      if (String(err).includes("denied")) {
        toast.message("Notifications blocked", {
          description: "Enable them in your browser site settings.",
        });
      } else {
        toast.message("Could not enable reminders", { description: String(err) });
      }
    }
  };

  return (
    <div className="mobile-shell grain" data-testid="dashboard">
      <Toaster position="top-center" />

      {/* Header strip */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-6">
        <div className="flex items-center gap-2">
          <Leaf className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          <span className="font-heading text-base font-semibold text-[#2A332C]">
            Skin Garden
          </span>
        </div>
        <button
          data-testid="notif-toggle"
          onClick={requestNotifications}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
            notifGranted ? "bg-[#E9F0E8] text-[#4A6B53]" : "bg-white text-[#6B7A70] border border-[#E9EFEA]"
          }`}
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      <div className="pb-32 pt-2 relative z-10">
        {tab === "today" && (
          <TodayView
            entry={todayEntry}
            setEntry={updateEntry}
            dayNum={dayNum}
            streak={streak}
            stage={stage}
            totalCompleted={totalCompleted}
            startDate={startDate}
            morningItems={morningItems}
            nightItems={nightItems}
          />
        )}
        {tab === "water" && (
          <WaterView
            entry={todayEntry}
            setEntry={updateEntry}
            profile={user}
            entries={entries}
            todayKey={todayKey}
          />
        )}
        {tab === "journey" && (
          <JourneyView
            entries={[...entries.filter((x) => x.date !== todayKey), { ...todayEntry, date: todayKey }]}
            streak={streak}
            startDate={startDate}
            dayNum={dayNum}
            morningItems={morningItems}
            nightItems={nightItems}
            photos={photos}
            waterGoal={user?.water_goal || 8}
            onEntryUpdate={handleEntryUpdate}
          />
        )}
        {tab === "selfies" && (
          <SelfiesView photos={photos} setPhotos={setPhotos} dayNum={dayNum} />
        )}
        {tab === "health" && (
          <HealthView
            entry={todayEntry}
            setEntry={updateEntry}
            profile={user}
            setProfile={setUser}
            onLogout={logout}
            morningItems={morningItems}
            nightItems={nightItems}
            isIOS={isIOS}
            isStandalone={isStandalone}
          />
        )}
      </div>

      <BottomNav active={tab} onChange={setTab} />
    </div>
  );
}
